import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAdminContent } from "@/context/AdminContentContext";
import { Field, Panel, TextArea, TextInput } from "@/components/admin/Field";

function StoreSync() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/store/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ count: 0 })), []);
  useEffect(() => { load(); }, [load]);
  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/store/refresh");
      toast.success(`${data.count} pacotes importados da Tebex.`);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="Importação automática da Tebex" description="Os pacotes, preços e benefícios são lidos da loja pública de hora a hora e sempre que o site precisa. Não há nada para editar aqui: altera na Tebex e o site acompanha.">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-eden-border bg-eden-bg px-4 py-3" data-testid="store-sync-status">
        <p className="text-sm text-eden-secondary">
          <span className="font-semibold text-eden-text" data-testid="store-sync-count">{status ? `${status.count} pacotes` : "…"}</span>
          {status?.synced_at && <> · última leitura {formatDate(status.synced_at)}</>}
          {status?.changed_at && <> · última alteração detetada {formatDate(status.changed_at)}</>}
          {status?.last_error && <span className="text-amber-300"> · última tentativa falhou</span>}
        </p>
        <button type="button" onClick={refresh} disabled={busy} className="btn-secondary h-9" data-testid="store-sync-refresh"><RefreshCw size={14} className={busy ? "animate-spin" : ""} /> {busy ? "A ler…" : "Atualizar agora"}</button>
      </div>
      {status?.packages?.length > 0 && (
        <ul className="grid gap-2 sm:grid-cols-2" data-testid="store-sync-packages">
          {status.packages.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 rounded-sm border border-eden-border px-3 py-2 text-sm">
              <span className="truncate text-eden-text">{/vip/i.test(p.category) ? `VIP ${p.name}` : p.tagline || p.name}</span>
              <span className="shrink-0 font-mono text-xs text-eden-orange">{new Intl.NumberFormat("pt-PT", { style: "currency", currency: p.currency || "EUR" }).format(p.price || 0)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export default function EditLoja() {
  const { draft, update } = useAdminContent();
  const s = draft.store;
  const set = (k) => (v) => update("store", { [k]: v });
  return (
    <>
      <Panel title="Textos da loja" description="A ligação para a Tebex é definida em Ligações & IDs; o vídeo de fundo em Imagens & vídeo.">
        <Field label="Etiqueta superior" id="store-eyebrow"><TextInput id="store-eyebrow" value={s.eyebrow} onChange={set("eyebrow")} maxLength={60} /></Field>
        <Field label="Título" id="store-title"><TextInput id="store-title" value={s.title} onChange={set("title")} maxLength={160} /></Field>
        <Field label="Texto" id="store-text"><TextArea id="store-text" value={s.text} onChange={set("text")} maxLength={800} /></Field>
        <Field label="Texto do botão" id="store-button"><TextInput id="store-button" value={s.button_label} onChange={set("button_label")} maxLength={40} /></Field>
        <Field label="Nota (opcional)" id="store-note"><TextInput id="store-note" value={s.note} onChange={set("note")} maxLength={300} /></Field>
      </Panel>
      <StoreSync />
    </>
  );
}
